﻿import { autorun, toJS } from 'mobx';
import { Collection, Map, MapBrowserEvent } from 'ol';
import BaseLayer from 'ol/layer/Base';
import { Options as BaseImageOptions } from 'ol/layer/BaseImage';
import LayerGroup from 'ol/layer/Group';
import ImageLayer from 'ol/layer/Image';
import { Pixel } from 'ol/pixel';
import ImageSource from 'ol/source/Image';
import { Operation, RasterSourceEvent } from 'ol/source/Raster';
import XYZSource from 'ol/source/XYZ';
import { IChartState } from "../../models/ChartModels";
import { InterpolationType } from '../../models/ColorScale';
import { DEFAULT_COLOR, getColorScaleMap, RGBAColor} from '../../models/ColorScales';
import { MobxReactionDisposer } from '../../models/MobxReactionDisposer';
import ViewUtilities from "../../utilities/ViewUtilities";
import { RasterSource } from './RasterSource';

interface IParams {
    defaultColor: RGBAColor,
    values: number[];
    colors: RGBAColor[];
    extendAbove?: RGBAColor;
    extendBelow?: RGBAColor;
    resolution: number;
    hillShade: boolean;
    vert: number;
    sunEl: number;
    sunAz: number;
    depths: (number | undefined)[][];
}

export interface Options extends Omit<BaseImageOptions<ImageSource>, 'source'> {
    source: XYZSource,
}

class DepthLayerBase extends ImageLayer<ImageSource> {

    protected source: XYZSource;
    protected raster: RasterSource;

    protected colors: RGBAColor[] = [];
    protected values: number[] = [];
    protected types: InterpolationType[] = [];

    protected extendAboveColor?: RGBAColor;
    protected extendBelowColor?: RGBAColor;

    protected mobxDisposer = new MobxReactionDisposer();

    private lastParams: IParams | undefined;

    constructor(
        protected state: IChartState,
        options: Options) {

        super({
            maxResolution: ViewUtilities.getResolutionFromZoom(2),
            ...options,
            source: undefined
        });

        this.source = options.source;

        this.raster = new RasterSource({
            buffer: 100,
            sources: [this.source],
            threads: 1,
            //Note: using lib breaks code after minification! This issue has been possibly resolved in ol 6.9.0.
            //lib: { getDepthValue, getColor },
            operationType: "image" as any,
            //Important: Code in this function runs in a worker, you can only use data passed through params and libs in here!            
            operation: ((inputs: ImageData[], params: IParams) => {

                function getHeight(image: ImageData, x: number, y: number, params: IParams) {

                    var i = (x + y * image.width) * 4;
                    if (image.data[i + 3] > 0) {
                        var v = (image.data[i] << 16 | image.data[i + 1] << 8 | image.data[i + 2]);
                        // if first bit is set, convert to a negative value
                        if (v & (1 << 23)) {
                            v = -1 - ((~v) & 0xFFFFFF);
                        }

                        return v * 0.01; // scale factor set by server
                    } else {
                        return null;
                    }
                }

                function getColor(height: number, params: IParams) {
                    const { values, colors, defaultColor } = params;


                    // assume values going from high to low
                    // find the index of the colorscale
                    let rowIndex = undefined;
                    if (height <= values[0] && height >= values[values.length - 1]) {
                        rowIndex = binarySearch(values, height);
                    }

                    if (rowIndex === undefined) {
                        if (height > values[0])
                            return params.extendAbove !== undefined ? params.extendAbove : defaultColor;
                        else
                            return params.extendBelow !== undefined ? params.extendBelow : defaultColor;
                    }

                    return colors[Math.min(rowIndex, colors.length - 1)];
                }

                function binarySearch(
                    values: number[],
                    value: number,
                    start: number = 0,
                    end: number = values.length - 1
                ): number
                {
                    const middle = Math.ceil((start + end) / 2);

                    if (middle > 0 && (values[middle - 1] >= value && values[middle] <= value)) {
                        return middle - 1;
                    }

                    if (values[middle] < value) {
                        return binarySearch(values, value, start, middle - 1);
                    }

                    if (values[middle] > value) {
                        return binarySearch(values, value, middle + 1, end);
                    }

                    // this should never happen
                    throw new Error("Something is wrong with finding the right index of the colorscale with height: " + value);
                };

                const image = inputs[0];
                const data = image.data;
                const output = new Uint8ClampedArray(data.length);

                params.depths = [];

                if (params.colors) {

                    if (params.hillShade) {

                        const dp = params.resolution * 2;
                        const twoPi = 2 * Math.PI;
                        const halfPi = Math.PI / 2;
                        const maxX = image.width - 1;
                        const maxY = image.height - 1;

                        const sunEl = (Math.PI * params.sunEl) / 180;
                        const sunAz = (Math.PI * params.sunAz) / 180;
                        const cosSunEl = Math.cos(sunEl);
                        const sinSunEl = Math.sin(sunEl);

                        for (let y = 0; y <= maxY; ++y) {
                            const y0 = y === 0 ? 0 : y - 1;
                            const y1 = y === maxY ? maxY : y + 1;

                            for (let x = 0; x <= maxX; ++x) {

                                const height = getHeight(image, x, y, params);

                                if (height === null) continue;

                                params.depths[x] = params.depths[x] ?? [];
                                params.depths[x][y] = height;

                                const x0 = x === 0 ? 0 : x - 1;
                                const x1 = x === maxX ? maxX : x + 1;
                                const d0 = getHeight(image, x0, y, params);
                                const d1 = getHeight(image, x1, y, params);

                                if (d0 === null || d1 === null) continue;

                                const dzdx = params.vert * (d1 - d0) / dp;

                                const d2 = getHeight(image, x, y0, params);
                                const d3 = getHeight(image, x, y1, params);

                                if (d2 === null || d3 === null) continue;

                                const dzdy = params.vert * (d3 - d2) / dp;

                                const slope = Math.atan(Math.sqrt(dzdx * dzdx + dzdy * dzdy));

                                let aspect = Math.atan2(dzdy, -dzdx);
                                if (aspect < 0) {
                                    aspect = halfPi - aspect;
                                } else if (aspect > halfPi) {
                                    aspect = twoPi - aspect + halfPi;
                                } else {
                                    aspect = halfPi - aspect;
                                }

                                const cosIncidence = sinSunEl * Math.cos(slope) + cosSunEl * Math.sin(slope) * Math.cos(sunAz - aspect);

                                const color = getColor(height, params);
                                // 0.7 is the cos incidence for "flat", this should scale to 1
                                const brightness = cosIncidence + 0.3;

                                const offset = (y * image.width + x) * 4;
                                output[offset] = Math.min(255, Math.max(0, brightness * color[0]));
                                output[offset + 1] = Math.min(255, Math.max(0, brightness * color[1]));
                                output[offset + 2] = Math.min(255, Math.max(0, brightness * color[2]));
                                output[offset + 3] = color[3];

                                // Debug mode to get a gray scale hillshading.
                                //var gray = Math.max(0, cosIncidence * 255);
                                //output[offset] = gray;
                                //output[offset + 1] = gray;
                                //output[offset + 2] = gray;
                                //output[offset + 3] = color[3];
                            }
                        }
                    } else {
                        for (let y = 0; y < image.height; y++) {
                            for (let x = 0; x < image.width; x++) {
                                const height = getHeight(image, x, y, params);

                                if (height === null) continue;

                                params.depths[x] = params.depths[x] ?? [];
                                params.depths[x][y] = height;

                                const color = getColor(height, params);
                                const offset = (y * image.width + x) * 4;
                                output[offset] = color[0];
                                output[offset + 1] = color[1];
                                output[offset + 2] = color[2];
                                output[offset + 3] = color[3];
                            }
                        }
                    }
                }
                else {
                    output.set(data);
                }

                return { data: output, width: image.width, height: image.height };
            }) as Operation
        });

        this.raster.on('beforeoperations', this.prepareRaster);
        this.raster.on('afteroperations', this.saveLastParams);
        this.setSource(this.raster);

        this.mobxDisposer.add([
            autorun(() => this.prepareRaster(null)),

            autorun(() => {
                if (this.state.bathymetry === undefined) {
                    return;
                }

                const colorScaleMap = getColorScaleMap(this.state.bathymetry);

                if (colorScaleMap) {
                    this.values = toJS(colorScaleMap.values);
                    this.colors = toJS(colorScaleMap.colors);

                    this.extendAboveColor = toJS(colorScaleMap.extendAbove);
                    this.extendBelowColor = toJS(colorScaleMap.extendBelow);
                }

                this.refresh();
            })
        ])
    }

    public dispose() {
        this.mobxDisposer.clear();
        super.dispose();
    }

    public refresh() {
        this.raster.refresh();
    }

    public reset() {
        this.source.refresh();
        this.raster.refresh();
    }

    public getDepthFromEvent(evt: MapBrowserEvent<UIEvent>) {
        return this.getDepth(evt.pixel, evt.frameState?.viewState.resolution ?? 0);
    }

    public getDepth(pixel: Pixel, resolution: number) {
        if (this.getVisible() && this.lastParams?.depths) {

            var multiplier = resolution / this.lastParams.resolution;
            if (isNaN(multiplier))
                multiplier = 1;

            const [col, row] = pixel.map(v => v * multiplier);

            const depths = this.lastParams.depths;
            const column = depths[Math.round(col + this.raster.buffer)];
            if (column)
                return column[Math.round(row + this.raster.buffer)];
        }
        return undefined;
    }

    // prepare raster data. The method doubles as a reaction to refresh the layer
    private prepareRaster = (event: RasterSourceEvent | null) => {
        var state = this.state.bathymetry;
        if (state) {
            var data: IParams = event?.data || {};

            data.resolution = event?.resolution ?? 1;
            data.vert = state.terrainExaggeration ?? 1;
            data.sunEl = 45;
            data.sunAz = 315;
            data.hillShade = state.hillShading;

            data.extendAbove = this.extendAboveColor;
            data.extendBelow = this.extendBelowColor;
            data.values = this.values;
            data.colors = this.colors;
            data.defaultColor = DEFAULT_COLOR;

            if (event && !event.data)
                event.data = data;
        }
        if (!event) {
            this.refresh();
        }
    }

    private saveLastParams = (event: RasterSourceEvent | null) => {
        this.lastParams = event?.data as IParams
    }
}

export default DepthLayerBase;

export const getDepthFromEvent = (evt: MapBrowserEvent<UIEvent>) => getDepthFromPixel(evt.map, evt.pixel, evt.frameState?.viewState.resolution ?? 0);

export const getDepthFromPixel = (map: Map, pixel: Pixel, resolution: number) => {

    const getDepth = (collection: Collection<BaseLayer>): number | undefined => {
        var depth: number | undefined = undefined;

        const layers = collection.getArray().slice().reverse().sort((a, b) => b.getZIndex() - a.getZIndex());
        for (var layer of layers) {
            if (layer instanceof DepthLayerBase)
                depth = layer.getDepth(pixel, resolution);
            else if (layer instanceof LayerGroup)
                depth = getDepth(layer.getLayers())
            if (depth !== undefined)
                break;
        }

        return depth;
    }

    return getDepth(map.getLayers());
}
